import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { streamToth } from "@/lib/tothStream";

export function useTothChat() {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const loadSessions = useCallback(async () => {
    const { data } = await api.get("/toth/chat/sessions");
    setSessions(data);
    return data;
  }, []);

  const open = useCallback(async (id) => {
    setSid(id);
    if (!id) {
      setMessages([]);
      return;
    }
    const { data } = await api.get(`/toth/chat/sessions/${id}`);
    setMessages(data.messages);
  }, []);

  useEffect(() => {
    loadSessions().then((d) => d[0] && open(d[0].id));
  }, [loadSessions, open]);

  const patchLast = (fn) => setMessages((ms) => [...ms.slice(0, -1), fn(ms[ms.length - 1])]);

  const send = async (text, contextPath) => {
    if (busyRef.current || !text.trim()) return;
    busyRef.current = true;
    setBusy(true);
    let id = sid;
    try {
      if (!id) {
        id = (await api.post("/toth/chat/sessions", {})).data.id;
        setSid(id);
      }
      setMessages((ms) => [...ms, { role: "user", content: text }, { role: "assistant", content: "", tools: [], proposals: [] }]);
      await streamToth(id, text, contextPath, (ev) => {
        if (ev.type === "delta") patchLast((m) => ({ ...m, content: m.content + ev.text }));
        if (ev.type === "tool") patchLast((m) => ({ ...m, tools: [...m.tools, ev.name] }));
        if (ev.type === "proposal") patchLast((m) => ({ ...m, proposals: [...m.proposals, { id: ev.id, title: ev.title }] }));
        if (ev.type === "error") patchLast((m) => ({ ...m, error: ev.message }));
      });
    } catch (e) {
      setMessages((ms) => (ms[ms.length - 1]?.role === "assistant" ? [...ms.slice(0, -1), { ...ms[ms.length - 1], error: e.message }] : [...ms, { role: "assistant", content: "", error: e.message }]));
    } finally {
      busyRef.current = false;
      setBusy(false);
      loadSessions();
    }
  };

  const archive = async (id) => {
    await api.delete(`/toth/chat/sessions/${id}`);
    const d = await loadSessions();
    open(d[0]?.id || null);
  };

  return { sessions, sid, messages, busy, open, send, archive };
}
